<script>
  import { createOffer } from '../lib/offers.js';
  import { workspace, callBbAi, refreshWorkspace, brainstormTurn } from '../lib/stores/workspace.js';
  import { showToast } from '../lib/stores/toast.js';
  import { ui } from '../lib/stores/ui.svelte.js';
  import MultiValueField from './MultiValueField.svelte';
  import CreateWalkthrough from './CreateWalkthrough.svelte';
  import MakeCompany from './MakeCompany.svelte';
  import { supabase } from '../lib/supabase.js';

  let mode = $state('imagine');
  let content = $state(ui.sparkDraft?.input || '');
  let loading = $state(false);
  let error = $state(null);
  let proposal = $state(ui.sparkDraft?.proposal || null);
  let sparkOriginalText = $state(ui.sparkDraft?.originalText || '');
  let sparkInstruction = $state(ui.sparkDraft?.instruction || '');
  let sparkRedirecting = $state(false);
  let sparkSaving = $state(false);
  let selectedItems = $state({ insights: [], audience: false, deal: false, plan: false });

  $effect(() => {
    ui.setSparkDraft({
      input: content,
      originalText: sparkOriginalText,
      proposal: proposal,
      instruction: sparkInstruction
    });
  });

  function toggleInsight(idx) {
    if (selectedItems.insights.includes(idx)) {
      selectedItems.insights = selectedItems.insights.filter(i => i !== idx);
    } else {
      selectedItems.insights = [...selectedItems.insights, idx];
    }
  }

  const hasSelection = $derived(
    selectedItems.insights.length > 0 ||
    selectedItems.audience ||
    selectedItems.deal ||
    selectedItems.plan
  );

  function selectAll() {
    selectedItems = {
      insights: (proposal?.insights || []).map((_, i) => i),
      audience: !!proposal?.audience_draft?.name,
      deal: !!proposal?.deal_draft?.name,
      plan: !!proposal?.plan_candidate?.title
    };
  }

  function discard() {
    if (!confirm('Discard this proposal? Your original text will be lost too.')) return;
    content = '';
    sparkOriginalText = '';
    proposal = null;
    sparkInstruction = '';
    selectedItems = { insights: [], audience: false, deal: false, plan: false };
    ui.clearSparkDraft();
  }

  function quickSaveAsOneInsight() {
    proposal = {
      summary: 'One insight from your original text',
      insights: [{ content: sparkOriginalText, tags: ['raw'] }],
      audience_draft: null,
      deal_draft: null,
      plan_candidate: null
    };
    selectedItems = { insights: [0], audience: false, deal: false, plan: false };
    showToast('Reframed as one insight — Save to commit', 'info');
  }

  function quickEachLineInsight() {
    const lines = sparkOriginalText.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length === 0) { showToast('No lines found', 'error'); return; }
    proposal = {
      summary: `${lines.length} insights, one per line`,
      insights: lines.map(line => ({ content: line, tags: [] })),
      audience_draft: null,
      deal_draft: null,
      plan_candidate: null
    };
    selectedItems = { insights: lines.map((_, i) => i), audience: false, deal: false, plan: false };
    showToast(`Reframed as ${lines.length} insights — Save to commit`, 'info');
  }

  async function saveSelected() {
    if (!hasSelection) {
      showToast('Pick what to save first', 'error');
      return;
    }
    sparkSaving = true;
    try {
      const promises = [];

      let insertedInsights = 0;
      if (selectedItems.insights.length > 0 && proposal.insights) {
        for (const idx of selectedItems.insights) {
          const it = proposal.insights[idx];
          if (!it?.content) continue;
          const { data, error } = await supabase.from('bb_insights').insert({
            workspace_id: workspace.id,
            content: it.content,
            tags: it.tags || (it.kind ? [it.kind] : []),
            source: 'manual',
            attached_to_kind: 'floating'
          }).select().single();
          if (error) { console.error('[insight insert]', error, { workspace_id: workspace.id, payload: it }); throw error; }
          if (!data) { console.error('[insight insert] returned no row — RLS may have blocked SELECT'); }
          insertedInsights++;
        }
        console.log(`[saveSelected] inserted ${insertedInsights} insights to workspace ${workspace.id}`);
      }

      if (selectedItems.audience && proposal.audience_draft?.name) {
        const a = proposal.audience_draft;
        promises.push(supabase.from('bb_audiences').insert({
          workspace_id: workspace.id,
          name: a.name, want: a.want, problem: a.problem,
          currently_doing: a.currently_doing, cost_of_current: a.cost_of_current,
          gap: a.gap, willing_to_give: a.willing_to_give,
          status: 'draft'
        }));
      }

      if (selectedItems.deal && proposal.deal_draft?.name) {
        const d = proposal.deal_draft;
        promises.push(createOffer(d));
      }

      if (selectedItems.plan && proposal.plan_candidate?.title) {
        const p = proposal.plan_candidate;
        promises.push(supabase.from('bb_plans').insert({
          workspace_id: workspace.id,
          title: p.title, body: p.body || '',
          attached_to_kind: 'floating', ai_generated: true
        }));
      }

      const results = await Promise.all(promises);
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        throw new Error(errors.map(e => e.error.message).join('; '));
      }

      showToast('Saved to library', 'success');
      content = '';
      sparkOriginalText = '';
      proposal = null;
      sparkInstruction = '';
      selectedItems = { insights: [], audience: false, deal: false, plan: false };
      ui.clearSparkDraft();
      await refreshWorkspace();
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally { sparkSaving = false; }
  }
  let insightTags = $state('');
  let audienceFields = $state({ name: '', want: '', problem: '', currently_doing: '', cost_of_current: '', gap: '', willing_to_give: '' });
  let dealFields = $state({ name: '', what_it_is: '', how_it_works: '', result: '', price: '', real_cost: '' });
  let stepFields = $state({ step_name: '', step_description: '', purpose: '', tags_input: '' });

  let urlInput = $state('');
  let urlLoading = $state(false);

  let voiceHelpOpen = $state(false);
  let voiceHelpOS = $state('mac');

  let imagineInput = $state('');
  let imagineLoading = $state(false);

  async function submitImagine() {
    const trimmed = imagineInput.trim();
    if (!trimmed) return;
    imagineLoading = true;
    try {
      const r = await callBbAi('synthesize_plan', {
        workspace_id: workspace.id,
        source_text: trimmed,
        save: true
      });
      if (r?.error) throw new Error(r.detail?.message || r.error);
      showToast(`Vision saved: "${r.title || 'Untitled'}"`, 'success');
      imagineInput = '';
      mode = null;
      await refreshWorkspace();
    } catch (e) {
      showToast('Imagine failed: ' + (e?.message || String(e)), 'error');
    } finally {
      imagineLoading = false;
    }
  }

  let showBrainstorm = $state(false);
  let brainstormMessages = $state([]);
  let brainstormDraft = $state('');
  let brainstormLoading = $state(false);
  let brainstormError = $state(null);
  let brainstormReady = $state(false);
  let brainstormPlan = $state(null);
  let brainstormPlanId = $state(null);

  function openBrainstorm() {
    showBrainstorm = true;
    if (brainstormMessages.length === 0) {
      brainstormMessages = [{
        role: 'assistant',
        content: "I'll help you find a business model in what you already know. Start anywhere — a problem you've noticed, a kind of person you want to help, an idea you've been kicking around, or just a feeling you can't shake. Plain words are fine."
      }];
    }
  }

  function closeBrainstorm() {
    showBrainstorm = false;
  }

  function resetBrainstorm() {
    brainstormMessages = [];
    brainstormDraft = '';
    brainstormReady = false;
    brainstormPlan = null;
    brainstormPlanId = null;
    brainstormError = null;
    openBrainstorm();
  }

  async function sendBrainstormMessage() {
    const text = brainstormDraft.trim();
    if (!text || brainstormLoading) return;
    brainstormError = null;
    brainstormLoading = true;
    brainstormMessages = [...brainstormMessages, { role: 'user', content: text }];
    brainstormDraft = '';
    try {
      const result = await brainstormTurn(brainstormMessages);
      if (result?.error) {
        brainstormError = result.error;
      } else if (result?.mode === 'ready') {
        brainstormMessages = [...brainstormMessages, { role: 'assistant', content: result.message }];
        brainstormPlan = { title: result.plan_title || 'Plan from brainstorm', body: result.plan_text || '' };
        brainstormReady = true;
        try {
          const planRes = await callBbAi('synthesize_plan', {
            workspace_id: workspace.id,
            source_text: brainstormPlan.body,
            attached_to_kind: 'floating',
            save: true
          });
          if (planRes?.plan_id) {
            brainstormPlanId = planRes.plan_id;
            await refreshWorkspace();
          }
        } catch (e) {
          console.warn('plan save failed', e);
        }
      } else {
        brainstormMessages = [...brainstormMessages, { role: 'assistant', content: result.message || '' }];
      }
    } catch (e) {
      brainstormError = e.message || String(e);
    } finally {
      brainstormLoading = false;
    }
  }

  async function autoGenerateFromPlan() {
    if (!brainstormPlanId) return;
    brainstormLoading = true;
    try {
      const r = await callBbAi('plan_action', {
        plan_id: brainstormPlanId,
        action: 'auto_generate',
        workspace_id: workspace.id
      });
      if (r?.error) throw new Error(r.error);
      await refreshWorkspace();
      showToast('Routes built — tap See the map', 'success');
      showBrainstorm = false;
    } catch (e) {
      showToast('Build failed: ' + e.message, 'error');
    } finally {
      brainstormLoading = false;
    }
  }

  function useAsSpark() {
    content = brainstormPlan?.body || '';
    mode = 'spark';
    showBrainstorm = false;
    showToast('Vision loaded as a spark — review and submit', 'info');
  }

  $effect(() => {
    const handler = () => { openBrainstorm(); };
    window.addEventListener('go-brainstorm', handler);
    return () => window.removeEventListener('go-brainstorm', handler);
  });

  $effect(() => {
    const handler = (e) => {
      if (['audience','offer','focus','spark','imagine','deal','step','url'].includes(e.detail)) mode = e.detail;
    };
    window.addEventListener('set-create-mode', handler);
    return () => window.removeEventListener('set-create-mode', handler);
  });

  function reset() {
    content = '';
    proposal = null;
    error = null;
    sparkOriginalText = '';
    sparkInstruction = '';
    audienceFields = { name: '', want: '', problem: '', currently_doing: '', cost_of_current: '', gap: '', willing_to_give: '' };
    dealFields = { name: '', what_it_is: '', how_it_works: '', result: '', price: '', real_cost: '' };
    stepFields = { step_name: '', step_description: '', purpose: '', tags_input: '' };
    insightTags = '';
    urlInput = '';
  }

  async function submitUrl() {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    let validUrl;
    try {
      validUrl = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    } catch {
      showToast("That doesn't look like a valid URL", 'error');
      return;
    }
    urlLoading = true;
    try {
      const r = await callBbAi('build_chain_from_url', {
        url: validUrl.toString(),
        workspace_id: workspace.id
      });
      if (r?.error) {
        const detail = r.detail?.message || r.detail || r.error;
        throw new Error(detail);
      }
      proposal = r;
    } catch (e) {
      showToast('URL fetch failed: ' + e.message, 'error');
    } finally {
      urlLoading = false;
    }
  }

  async function saveSpark() {
    if (!content.trim()) return;
    sparkOriginalText = content.trim();
    loading = true; error = null;
    try {
      const result = await callBbAi('parse_spark', { content, workspace_id: workspace.id });
      proposal = result;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  function backToEdit() {
    proposal = null;
    sparkInstruction = '';
    if (!content && sparkOriginalText) content = sparkOriginalText;
  }

  async function submitRedirect() {
    const instruction = sparkInstruction.trim();
    if (!instruction) return;
    sparkRedirecting = true;
    try {
      const r = await callBbAi('parse_spark_redirect', {
        workspace_id: workspace.id,
        content: sparkOriginalText,
        instruction
      });
      if (r?.error) throw new Error(r.detail?.message || r.error);
      proposal = r;
      sparkInstruction = '';
      showToast('Got new options — pick what to save below', 'info');
    } catch (e) {
      showToast('Redirect failed: ' + e.message, 'error');
    } finally { sparkRedirecting = false; }
  }

  async function buildFromProposal(option) {
    loading = true; error = null;
    try {
      if (option === 'build_full_chain') {
        await callBbAi('build_full_chain', {
          workspace_id: workspace.id,
          audience_draft: proposal.audience_draft,
          deal_draft: proposal.deal_draft || proposal.offer_draft
        });
        await refreshWorkspace();
        reset();
        showToast('Routes built — tap See the map', 'success');
        return;
      } else if (option === 'build_only_audience') {
        await supabase.from('bb_audiences').insert({
          workspace_id: workspace.id,
          ...proposal.audience_draft
        });
        await refreshWorkspace();
        reset();
        showToast('Audience saved to library', 'success');
        return;
      } else if (option === 'build_only_deal' || option === 'build_only_offer') {
        await createOffer(proposal.deal_draft || proposal.offer_draft);
        await refreshWorkspace();
        reset();
        showToast('Offer saved to library', 'success');
        return;
      } else if (option === 'save_insights' || option === 'save_focuses') {
        const items = proposal.insights || proposal.focuses || [];
        for (const it of items) {
          await supabase.from('bb_insights').insert({
            workspace_id: workspace.id,
            content: it.content,
            tags: it.tags || (it.kind ? [it.kind] : []),
            source: 'ai_distilled',
            attached_to_kind: 'floating'
          });
        }
        await refreshWorkspace();
        reset();
        showToast('Insights saved to library', 'success');
        return;
      } else if (option === 'save_plan') {
        const planText = proposal.plan_text || proposal.plan_candidate || proposal.summary || '';
        await supabase.from('bb_plans').insert({
          workspace_id: workspace.id,
          title: proposal.plan_title || 'Plan from spark',
          body: planText,
          attached_to_kind: 'floating',
          ai_generated: true
        });
        await refreshWorkspace();
        reset();
        showToast('Vision saved', 'success');
        return;
      }
      await refreshWorkspace();
      reset();
    } catch (e) {
      error = e.message;
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      loading = false;
    }
  }

  async function saveAudience() {
    if (!audienceFields.name.trim()) { error = 'Name required'; return; }
    loading = true; error = null;
    try {
      await supabase.from('bb_audiences').insert({
        workspace_id: workspace.id,
        ...audienceFields
      });
      await refreshWorkspace();
      reset();
      showToast('Audience saved to library', 'success');
    } catch (e) {
      error = e.message;
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      loading = false;
    }
  }

  async function saveDeal() {
    if (!dealFields.name.trim()) { error = 'Name required'; return; }
    loading = true; error = null;
    try {
      await createOffer(dealFields);
      await refreshWorkspace();
      reset();
      showToast('Offer saved to library', 'success');
    } catch (e) {
      error = e.message;
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      loading = false;
    }
  }

  async function saveStep() {
    if (!stepFields.step_name.trim()) return;
    loading = true; error = null;
    try {
      const tags = stepFields.tags_input.split(',').map(t => t.trim()).filter(Boolean);
      const { error: e } = await supabase.from('bb_steps').insert({
        workspace_id: workspace.id,
        step_name: stepFields.step_name.trim(),
        step_description: stepFields.step_description.trim() || null,
        purpose: stepFields.purpose.trim() || null,
        tags,
        status: 'draft',
        state: 'user'
      });
      if (e) throw e;
      await refreshWorkspace();
      reset();
      showToast('Step saved to library', 'success');
    } catch (e) {
      error = e.message;
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      loading = false;
    }
  }

  async function saveInsight() {
    if (!content.trim()) return;
    loading = true; error = null;
    try {
      const tags = insightTags.split(',').map(t => t.trim()).filter(Boolean);
      await supabase.from('bb_insights').insert({
        workspace_id: workspace.id,
        content,
        tags,
        source: 'manual',
        attached_to_kind: 'floating'
      });
      await refreshWorkspace();
      reset();
      showToast('Insight saved to library', 'success');
    } catch (e) {
      error = e.message;
      showToast('Save failed: ' + e.message, 'error');
    } finally {
      loading = false;
    }
  }
</script>

<div class="create-page">
  {#if proposal}
    <!-- ───── Spark proposal (page 4) ───── -->
    <div class="proposal-wrap">
      <div class="spark-proposal-header">
        <h3>Here's what I found</h3>
        <button class="spark-back-btn" onclick={backToEdit} type="button" title="Back to your text">← Back to edit</button>
      </div>
      {#if sparkOriginalText}
        <details class="spark-original">
          <summary>Your input</summary>
          <pre class="spark-original-text">{sparkOriginalText}</pre>
        </details>
      {/if}
      <div class="collapsed-spark plastic-card">
        <span class="pill-eyebrow">{proposal.source_url ? 'From URL' : 'Your spark'}</span>
        {#if proposal.source_url}
          <div class="proposal-source">Source: <a href={proposal.source_url} target="_blank" rel="noopener">{proposal.source_url}</a></div>
        {/if}
        <p class="spark-text">{proposal.summary || ''}</p>
      </div>

      <div class="glow-frame">
        <div class="proposal-card plastic-card">
          <span class="pill-eyebrow gold-pill">Elvie's proposal</span>
          <h2 class="proposal-title">Pick what to save</h2>

          {#if proposal.audience_draft?.name}
            <button class="prop-card prop-audience" class:selected={selectedItems.audience} onclick={() => selectedItems.audience = !selectedItems.audience} type="button">
              <div class="prop-check">{selectedItems.audience ? '✓' : ''}</div>
              <div class="prop-body-wrap">
                <div class="prop-kind">AUDIENCE</div>
                <h4 class="prop-title">{proposal.audience_draft.name}</h4>
                {#if proposal.audience_draft.want}
                  <p class="prop-line">wants: {proposal.audience_draft.want}</p>
                {/if}
              </div>
            </button>
          {/if}

          {#if (proposal.deal_draft || proposal.offer_draft)?.name}
            {@const dd = proposal.deal_draft || proposal.offer_draft}
            <button class="prop-card prop-deal" class:selected={selectedItems.deal} onclick={() => selectedItems.deal = !selectedItems.deal} type="button">
              <div class="prop-check">{selectedItems.deal ? '✓' : ''}</div>
              <div class="prop-body-wrap">
                <div class="prop-kind">OFFER</div>
                <h4 class="prop-title">{dd.name}</h4>
                {#if dd.what_it_is}
                  <p class="prop-line">{dd.what_it_is}</p>
                {/if}
              </div>
            </button>
          {/if}

          {#if (proposal.insights?.length || proposal.focuses?.length)}
            {@const items = proposal.insights || proposal.focuses}
            <div class="prop-section-label">INSIGHTS EXTRACTED</div>
            {#each items as ins, idx}
              <button class="prop-card prop-insight" class:selected={selectedItems.insights.includes(idx)} onclick={() => toggleInsight(idx)} type="button">
                <div class="prop-check">{selectedItems.insights.includes(idx) ? '✓' : ''}</div>
                <div class="prop-body-wrap">
                  {#if ins.tags && ins.tags.length > 0}
                    <div class="prop-kind">{ins.tags.join(' · ')}</div>
                  {:else if ins.kind}
                    <div class="prop-kind">{ins.kind}</div>
                  {/if}
                  <p class="prop-line">{ins.content}</p>
                </div>
              </button>
            {/each}
          {/if}

          {#if proposal.plan_candidate?.title}
            <button class="prop-card prop-plan" class:selected={selectedItems.plan} onclick={() => selectedItems.plan = !selectedItems.plan} type="button">
              <div class="prop-check">{selectedItems.plan ? '✓' : ''}</div>
              <div class="prop-body-wrap">
                <div class="prop-kind">VISION</div>
                <h4 class="prop-title">{proposal.plan_candidate.title}</h4>
                {#if proposal.plan_candidate.body}
                  <p class="prop-body">{proposal.plan_candidate.body}</p>
                {/if}
              </div>
            </button>
          {:else if proposal.plan_text}
            <button class="prop-card prop-plan" class:selected={selectedItems.plan} onclick={() => selectedItems.plan = !selectedItems.plan} type="button">
              <div class="prop-check">{selectedItems.plan ? '✓' : ''}</div>
              <div class="prop-body-wrap">
                <div class="prop-kind">VISION</div>
                <h4 class="prop-title">{proposal.plan_title || 'Vision from spark'}</h4>
                <p class="prop-body">{proposal.plan_text}</p>
              </div>
            </button>
          {/if}

          <div class="spark-actions">
            <button class="spark-action-secondary" onclick={selectAll} type="button">Select all</button>
            <button class="spark-action-secondary" onclick={backToEdit} type="button">← Back to edit</button>
            <div class="spark-actions-spacer"></div>
            <button class="spark-action-tertiary" onclick={discard} type="button">Discard all</button>
            <button class="spark-action-primary" onclick={saveSelected} disabled={!hasSelection || sparkSaving} type="button">
              {sparkSaving ? 'Saving…' : 'Save selected'}
            </button>
          </div>

          {#if sparkOriginalText}
            <div class="spark-quick-redirects">
              <span class="spark-quick-label">Quick reframe:</span>
              <button class="spark-quick-btn" onclick={quickSaveAsOneInsight} type="button">Save as 1 insight</button>
              <button class="spark-quick-btn" onclick={quickEachLineInsight} type="button">Each line as insight</button>
            </div>
          {/if}

          {#if error}<div class="error">{error}</div>{/if}
        </div>
      </div>

      <div class="spark-redirect">
        <label class="spark-redirect-label">Not what you wanted? Tell Elvie what to do with this.</label>
        <div class="spark-redirect-row">
          <textarea
            class="spark-redirect-input"
            bind:value={sparkInstruction}
            placeholder="e.g. Save the 7 types as one insight tagged 'goals/target market'"
            rows="2"
            disabled={sparkRedirecting}
          ></textarea>
          <button
            class="spark-redirect-btn"
            onclick={submitRedirect}
            disabled={sparkRedirecting || !sparkInstruction.trim()}
            type="button"
          >
            {sparkRedirecting ? '…' : 'Redirect'}
          </button>
        </div>
      </div>
    </div>
  {:else}
    <!-- ───── Create (Miles 2026-11-05: Home is Create — start a business or imagine) ───── -->
    <MakeCompany />
    <div class="quick-eyebrow"><span class="pill-eyebrow">Create</span></div>

    <button class="brainstorm-launch" onclick={openBrainstorm} type="button">
      <span class="bs-icon">✶</span>
      <span class="bs-text">Help me start a business model</span>
      <span class="bs-arrow">→</span>
    </button>

    <div class="entry-modes">
      <div class="qp-imagine-wrap glow-frame compact tint-rainbow">
        <button
          class="qp-pill qp-imagine"
          class:active={mode === 'imagine'}
          onclick={() => mode = 'imagine'}
          data-walk="imagine"
          type="button"
        >
          <span class="qp-icon">💭</span>
          <span class="qp-label">Imagine</span>
          <span class="qp-sub">Describe your dream — Elvie writes the vision</span>
        </button>
      </div>

      <div class="qp-row">
        <div class="qp-wrap glow-frame compact tint-mono"><button data-walk="spark" class="quick-pill qp-spark" class:active={mode==='spark'} onclick={() => mode='spark'} type="button">⚡ Spark</button></div>
        <div class="qp-wrap glow-frame compact tint-mono"><button data-walk="url" class="quick-pill qp-url" class:active={mode==='url'} onclick={() => mode='url'} type="button">🔗 Your site</button></div>
      </div>
    </div>

    {#if mode === 'spark'}
      <div class="glow-frame tint-mono">
        <div class="composer plastic-card kind-card kind-mono">
          <span class="pill-eyebrow">Dump your spark</span>
          <div class="glow-frame compact tint-mono">
            <div class="textarea-wrap">
              <textarea bind:value={content} rows="10" placeholder="A quick observation, idea, or note about who you serve, what you offer, or something you noticed. We'll parse it into audiences, offers, and insights." autofocus></textarea>
              <button class="voice-hint-btn" onclick={() => voiceHelpOpen = true} type="button" title="Use voice to type">
                🎙 Voice
              </button>
            </div>
          </div>
          <div class="composer-foot">
            <span class="word-count">{content.trim() ? content.trim().split(/\s+/).length : 0} / ~</span>
          </div>
        </div>
      </div>

      <div class="submit-row">
        <button class="parse-bolt" onclick={saveSpark} disabled={loading || !content.trim()}>
          <svg width="18" height="22" viewBox="0 0 58 76" aria-hidden="true">
            <path d="M34 2 L8 42 L24 42 L18 74 L50 30 L32 30 L40 2 Z" fill="#0a0a0c"/>
          </svg>
          {loading ? 'Sending to Elvie…' : 'Submit message to Elvie'}
        </button>
      </div>

    {:else if mode === 'imagine'}
      <div class="glow-frame tint-rainbow">
        <div class="form-card plastic-card imagine-entry">
          <label class="imagine-label">Describe what you want to build — your dream, end to end.</label>
          <textarea
            class="imagine-textarea"
            bind:value={imagineInput}
            placeholder="People come to me because [...]. They get [...]. Then [...]. The end result is [...]."
            rows="6"
            disabled={imagineLoading}
          ></textarea>
          <button class="imagine-submit" onclick={submitImagine} disabled={imagineLoading || !imagineInput.trim()} type="button">
            {imagineLoading ? 'Writing your vision…' : 'Write my vision'}
          </button>
          <div class="imagine-hint">Elvie writes your vision, then can turn it into routes. Spark is for quick observations.</div>
        </div>
      </div>

    {:else if mode === 'step'}
      <div class="glow-frame tint-purple">
        <div class="form-card plastic-card kind-card kind-purple">
          <span class="pill-eyebrow">New step</span>
          <div class="glow-frame compact tint-purple"><input bind:value={stepFields.step_name} placeholder="Step title" /></div>
          <div class="glow-frame compact tint-purple"><textarea bind:value={stepFields.step_description} placeholder="What happens at this step" rows="2"></textarea></div>
          <div class="glow-frame compact tint-purple"><textarea bind:value={stepFields.purpose} placeholder="Purpose — why this step exists" rows="2"></textarea></div>
          <div class="glow-frame compact tint-purple"><input bind:value={stepFields.tags_input} placeholder="Type (e.g. onboarding, consultation, decision) — comma-separated" /></div>
          <div class="submit-row">
            <button class="primary-btn purple-btn" onclick={saveStep} disabled={loading || !stepFields.step_name.trim()}>
              {loading ? 'Saving…' : 'Save step'}
            </button>
          </div>
        </div>
      </div>

    {:else if mode === 'audience'}
      <div class="glow-frame tint-blue">
        <div class="form-card plastic-card kind-card kind-blue audience-form">
          <span class="pill-eyebrow blue-pill">New audience</span>
          <div class="glow-frame compact tint-blue"><input bind:value={audienceFields.name} placeholder="Name (short label)" /></div>
          <div class="glow-frame compact tint-blue"><textarea bind:value={audienceFields.want} placeholder="What they want (deeper desire)" rows="2"></textarea></div>
          <div class="glow-frame compact tint-blue"><textarea bind:value={audienceFields.problem} placeholder="Their problem (surface pain)" rows="2"></textarea></div>
          <MultiValueField
            bind:value={audienceFields.currently_doing}
            label="What they're doing now"
            help="Add each alternative or behavior on its own line — your competition + their coping habits"
            placeholder="e.g. Reading Lenny's articles" />
          <MultiValueField
            bind:value={audienceFields.cost_of_current}
            label="The cost of doing it that way"
            help="What each of those is costing them"
            placeholder="e.g. Hours of reading without applying" />
          <MultiValueField
            bind:value={audienceFields.gap}
            label="What's still missing"
            help="What none of their current behaviors actually deliver"
            placeholder="e.g. Honest feedback on their specific business" />
          <MultiValueField
            bind:value={audienceFields.willing_to_give}
            label="What they'd trade for a real solution"
            help="Time, money, attention, or ego they'd put up"
            placeholder="e.g. $100 one-time" />
          <div class="submit-row">
            <button class="primary-btn blue-btn" onclick={saveAudience} disabled={loading || !audienceFields.name.trim()}>
              {loading ? 'Saving…' : 'Save audience'}
            </button>
          </div>
        </div>
      </div>

    {:else if mode === 'deal'}
      <div class="glow-frame tint-gold">
        <div class="form-card plastic-card kind-card kind-gold offer-form">
          <span class="pill-eyebrow gold-pill">New offer</span>
          <div class="glow-frame compact tint-gold"><input bind:value={dealFields.name} placeholder="Name" /></div>
          <div class="glow-frame compact tint-gold"><textarea bind:value={dealFields.what_it_is} placeholder="Value: the problem it solves, for whom" rows="2"></textarea></div>
          <div class="glow-frame compact tint-gold"><textarea bind:value={dealFields.how_it_works} placeholder="Where / how it's delivered" rows="2"></textarea></div>
          <div class="glow-frame compact tint-gold"><textarea bind:value={dealFields.result} placeholder="Promise: what is being given" rows="2"></textarea></div>
          <div class="glow-frame compact tint-gold"><input bind:value={dealFields.price} placeholder="Ask: the price as written" /></div>
          <MultiValueField
            bind:value={dealFields.real_cost}
            label="What the customer actually invests"
            help="Non-money costs: time, attention, behavior change, ego risk"
            placeholder="e.g. 30 minutes of focused thinking" />
          <div class="submit-row">
            <button class="primary-btn gold-btn" onclick={saveDeal} disabled={loading || !dealFields.name.trim()}>
              {loading ? 'Saving…' : 'Save offer'}
            </button>
          </div>
        </div>
      </div>

    {:else if mode === 'url'}
      <div class="glow-frame tint-blue">
        <div class="form-card plastic-card kind-card kind-blue url-entry">
          <span class="pill-eyebrow blue-pill">Read from URL</span>
          <label class="url-label">Paste a URL — your live site, a competitor, anything with a clear audience/offer</label>
          <div class="url-input-row">
            <input
              class="url-input"
              bind:value={urlInput}
              placeholder="https://example.com"
              onkeydown={(e) => { if (e.key === 'Enter') submitUrl(); }}
              disabled={urlLoading}
            />
            <button class="url-submit" onclick={submitUrl} disabled={urlLoading || !urlInput.trim()} type="button">
              {urlLoading ? 'Reading…' : 'Read site'}
            </button>
          </div>
          <div class="url-hint">Reads up to 150KB of visible text. JS-only sites and paywalls won't work.</div>
        </div>
      </div>
    {/if}

    {#if error}<div class="error">{error}</div>{/if}
  {/if}

  {#if voiceHelpOpen}
    <div class="voice-overlay" onclick={() => voiceHelpOpen = false}>
      <div class="voice-modal plastic-card" onclick={(e) => e.stopPropagation()}>
        <div class="voice-header">
          <h3>Type with your voice</h3>
          <button class="voice-close" onclick={() => voiceHelpOpen = false}>×</button>
        </div>
        <p class="voice-intro">Use your device's built-in voice control. It's faster than typing and works in any field.</p>
        <div class="voice-os-tabs">
          <button class:active={voiceHelpOS === 'mac'} onclick={() => voiceHelpOS = 'mac'}>Mac</button>
          <button class:active={voiceHelpOS === 'ios'} onclick={() => voiceHelpOS = 'ios'}>iPhone / iPad</button>
          <button class:active={voiceHelpOS === 'windows'} onclick={() => voiceHelpOS = 'windows'}>Windows</button>
          <button class:active={voiceHelpOS === 'android'} onclick={() => voiceHelpOS = 'android'}>Android</button>
        </div>
        <div class="voice-steps">
          {#if voiceHelpOS === 'mac'}
            <ol>
              <li>Open <strong>System Settings → Keyboard → Dictation</strong></li>
              <li>Turn <strong>Dictation</strong> on</li>
              <li>Set the shortcut to <strong>Press Control twice</strong> (or pick your own)</li>
              <li>Click into any text field here, then press the shortcut and start talking</li>
              <li>Press the shortcut again to stop</li>
            </ol>
            <p class="voice-tip">For full hands-free control, also try <strong>System Settings → Accessibility → Voice Control</strong>.</p>
          {:else if voiceHelpOS === 'ios'}
            <ol>
              <li>Open <strong>Settings → General → Keyboard</strong></li>
              <li>Turn <strong>Enable Dictation</strong> on</li>
              <li>Tap any text field here, then tap the <strong>microphone</strong> on the keyboard</li>
              <li>Talk — say "period", "comma", "new line" for punctuation</li>
              <li>Tap the keyboard icon to stop</li>
            </ol>
            <p class="voice-tip">For full voice control: <strong>Settings → Accessibility → Voice Control</strong>.</p>
          {:else if voiceHelpOS === 'windows'}
            <ol>
              <li>Press <strong>Windows + H</strong> in any text field</li>
              <li>Allow microphone access if prompted</li>
              <li>Start talking — Voice Typing handles punctuation automatically</li>
              <li>Click outside or press <strong>Windows + H</strong> again to stop</li>
            </ol>
            <p class="voice-tip">Need full voice control? Open <strong>Settings → Accessibility → Speech</strong>.</p>
          {:else if voiceHelpOS === 'android'}
            <ol>
              <li>Tap any text field here to open the keyboard</li>
              <li>Tap the <strong>microphone icon</strong> on the keyboard (usually top-right)</li>
              <li>Allow microphone access if prompted</li>
              <li>Start talking</li>
              <li>Tap the mic again to stop</li>
            </ol>
            <p class="voice-tip">If you don't see a mic, enable <strong>Voice Typing</strong> in your keyboard settings.</p>
          {/if}
        </div>
        <div class="voice-footer">
          <button class="voice-got-it" onclick={() => voiceHelpOpen = false}>Got it</button>
        </div>
      </div>
    </div>
  {/if}

  {#if showBrainstorm}
    <div class="bs-overlay" onclick={closeBrainstorm}>
      <div class="bs-modal" onclick={(e) => e.stopPropagation()}>
        <div class="brainstorm-elvie-header">
          <span class="brainstorm-elvie-avatar">💬</span>
          <div>
            <h3>Elvie</h3>
            <p class="brainstorm-elvie-subtitle">Help me start a business</p>
          </div>
          <button class="bs-close brainstorm-elvie-close" onclick={closeBrainstorm} type="button">×</button>
        </div>
        <div class="bs-header bs-header-sub">
          <div>
            <p class="bs-sub">Talk through what you've seen or experienced. Elvie will draw it out.</p>
          </div>
        </div>

        <div class="bs-thread">
          {#each brainstormMessages as msg, i}
            <div class="bs-msg bs-{msg.role}">
              {#if msg.role === 'assistant'}
                <span class="bs-avatar">✶</span>
              {/if}
              <div class="bs-bubble">{msg.content}</div>
            </div>
          {/each}
          {#if brainstormLoading}
            <div class="bs-msg bs-assistant">
              <span class="bs-avatar">✶</span>
              <div class="bs-bubble bs-typing"><span></span><span></span><span></span></div>
            </div>
          {/if}
          {#if brainstormError}
            <div class="bs-error">{brainstormError}</div>
          {/if}
        </div>

        {#if brainstormReady && brainstormPlan}
          <div class="bs-ready-card">
            <div class="bs-ready-label">Your vision</div>
            <div class="bs-ready-title">{brainstormPlan.title}</div>
            <div class="bs-ready-text">{brainstormPlan.body}</div>
            <div class="bs-ready-actions">
              <button class="bs-primary" onclick={autoGenerateFromPlan} disabled={brainstormLoading || !brainstormPlanId} type="button">
                {brainstormLoading ? 'Building…' : 'Auto-generate chain'}
              </button>
              <button class="bs-secondary" onclick={useAsSpark} type="button">Use as my spark</button>
              <button class="bs-secondary" onclick={resetBrainstorm} type="button">Start over</button>
            </div>
          </div>
        {:else}
          <div class="bs-input-row">
            <textarea
              bind:value={brainstormDraft}
              placeholder="Type your reply..."
              rows="2"
              disabled={brainstormLoading}
              onkeydown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendBrainstormMessage(); } }}
            ></textarea>
            <button class="bs-send" onclick={sendBrainstormMessage} disabled={brainstormLoading || !brainstormDraft.trim()} type="button">
              Send
            </button>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  <CreateWalkthrough />
</div>

<style>
  .create-page {
    max-width: 720px;
    width: 100%;
    margin: 0 auto;
    padding: 48px 24px 96px;
    font-family: var(--font-sans);
    color: var(--text);
  }

  .quick-eyebrow {
    display: flex;
    justify-content: center;
    margin-bottom: 16px;
  }
  .pill-eyebrow.gold-pill { color: var(--gold); border-color: var(--gold-line); }
  .pill-eyebrow.blue-pill { color: var(--blue); border-color: var(--blue-line); }

  .quick-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 28px;
  }
  .qp-wrap.glow-frame.compact { border-radius: 999px; }
  .qp-wrap.glow-frame.compact::after { border-radius: 999px; }
  .quick-pill {
    width: 100%;
    background: rgba(10,10,12,0.85);
    border: none;
    color: var(--text-muted);
    padding: 12px 14px;
    border-radius: 999px;
    cursor: pointer;
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.1px;
    transition: background 0.15s ease, color 0.15s ease;
    display: block;
    position: relative;
    z-index: 2;
  }
  .quick-pill:hover { color: var(--text); }
  /* Active fills with the kind's color */
  .quick-pill.qp-audience.active { background: var(--blue); color: #0a0a0c; font-weight: 700; }
  .quick-pill.qp-offer.active    { background: linear-gradient(180deg, #F4D03F 0%, #D4AF37 55%, #A8861A 100%); color: #0a0a0c; font-weight: 700; }
  .quick-pill.qp-focus.active    { background: var(--purple); color: #0a0a0c; font-weight: 700; }
  .quick-pill.qp-spark.active    { background: #f4f4f6; color: #0a0a0c; font-weight: 700; }
  .quick-pill.qp-url.active      { background: var(--blue); color: #0a0a0c; font-weight: 700; }
  .quick-pill.qp-imagine.active  { background: linear-gradient(90deg, #ff5e5e 0%, #ffae3c 18%, #ffe83c 36%, #58e07a 54%, #5ec1ff 72%, #b583f0 100%); color: #0a0a0c; font-weight: 700; }

  .entry-modes { display: flex; flex-direction: column; gap: 14px; align-items: center; margin-bottom: 18px; }
  .qp-imagine-wrap { width: 100%; max-width: 520px; border-radius: 14px; }
  .qp-imagine-wrap.glow-frame.compact { border-radius: 14px; }
  .qp-imagine-wrap.glow-frame.compact::after { border-radius: 14px; }
  .qp-pill.qp-imagine {
    width: 100%;
    padding: 18px 20px;
    display: flex; flex-direction: column; align-items: center; gap: 6px;
    background: linear-gradient(180deg, rgba(20,18,22,0.92), rgba(12,11,14,0.92));
    border: none;
    border-radius: 14px;
    cursor: pointer;
    color: inherit;
    font-family: inherit;
    margin: 0;
    box-shadow: none;
    transition: background 0.15s ease;
    position: relative;
    z-index: 2;
  }
  .qp-pill.qp-imagine:hover, .qp-pill.qp-imagine.active {
    background: linear-gradient(180deg, rgba(212,175,55,0.18), rgba(212,175,55,0.06));
  }
  .qp-pill.qp-imagine .qp-icon { font-size: 26px; }
  .qp-pill.qp-imagine .qp-label { font-family: 'DM Serif Display', serif; font-size: 19px; color: #f0efe9; letter-spacing: -0.01em; }
  .qp-pill.qp-imagine .qp-sub { font-size: 11px; color: #9aa0a6; font-family: 'JetBrains Mono', monospace; letter-spacing: 0.5px; }

  .qp-row { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; }

  .url-entry { padding: 24px; }
  .url-label {
    display: block;
    color: #d8d8dc;
    font-size: 13px;
    margin: 8px 0 10px;
    line-height: 1.5;
  }
  .url-input-row { display: flex; gap: 8px; }
  .url-input {
    flex: 1;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.1);
    color: #f4f4f6;
    padding: 10px 12px;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    box-sizing: border-box;
    margin: 0;
  }
  .url-input:focus { outline: none; border-color: rgba(122,184,255,0.4); }
  .url-submit {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c;
    border: none;
    padding: 10px 18px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    font-size: 13px;
    white-space: nowrap;
    margin: 0;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.25);
  }
  .url-submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .url-hint {
    margin-top: 8px;
    color: #6a6a6e;
    font-size: 11px;
    font-family: 'JetBrains Mono', monospace;
  }

  .proposal-source {
    padding: 6px 10px;
    background: rgba(122,184,255,0.08);
    border: 1px solid rgba(122,184,255,0.2);
    border-radius: 6px;
    color: #7AB8FF;
    font-size: 11px;
    font-family: 'JetBrains Mono', monospace;
    margin: 8px 0 4px;
    word-break: break-all;
  }
  .proposal-source a { color: inherit; text-decoration: underline; }

  .imagine-entry { display: flex; flex-direction: column; gap: 0; }
  .imagine-label { display: block; color: #d8d8dc; font-size: 13px; margin-bottom: 10px; font-weight: 500; }
  .imagine-textarea {
    width: 100%;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.1);
    color: #f4f4f6;
    padding: 12px 14px;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    resize: vertical;
    min-height: 140px;
    box-sizing: border-box;
    line-height: 1.55;
  }
  .imagine-textarea:focus {
    outline: none;
    border-color: rgba(212,175,55,0.4);
  }
  .imagine-submit {
    align-self: flex-start;
    margin-top: 12px;
    background: linear-gradient(90deg, #ff5e5e 0%, #ffae3c 18%, #ffe83c 36%, #58e07a 54%, #5ec1ff 72%, #b583f0 100%);
    color: #0a0a0c;
    border: none;
    padding: 11px 22px;
    border-radius: 10px;
    cursor: pointer;
    font-weight: 700;
    font-size: 13px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.4), 0 6px 22px rgba(180,131,240,0.28);
    transition: transform 0.1s ease;
  }
  .imagine-submit:hover:not(:disabled) { transform: translateY(-1px); }
  .imagine-submit:disabled { opacity: 0.45; cursor: not-allowed; transform: none; }
  .imagine-hint {
    margin-top: 10px;
    color: #6a6a6e;
    font-size: 11px;
    line-height: 1.5;
    font-style: italic;
  }

  .composer {
    padding: 22px 22px 16px;
  }
  .composer .pill-eyebrow {
    margin-bottom: 10px;
  }
  .textarea-wrap {
    position: relative;
    margin-top: 8px;
  }
  textarea, input, select {
    width: 100%;
    background: transparent;
    border: none;
    color: var(--text);
    font-family: var(--font-sans);
    font-size: 15px;
    line-height: 1.55;
    padding: 0;
    box-sizing: border-box;
    outline: none;
    resize: vertical;
  }
  textarea::placeholder, input::placeholder {
    color: var(--text-faint);
  }
  textarea:focus, input:focus, select:focus { outline: none; }

  .form-card {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .form-card input, .form-card textarea, .form-card select {
    background: #0a0a0c;
    border: none;
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 14px;
    color: var(--text);
    font-family: var(--font-sans);
    box-sizing: border-box;
    width: 100%;
    transition: background 0.15s ease;
    position: relative;
    z-index: 2;
    display: block;
  }
  .form-card .glow-frame.compact { background: #0a0a0c; }
  .form-card .glow-frame.compact.tint-blue   { box-shadow: 0 0 14px rgba(122,184,255,0.18); }
  .form-card .glow-frame.compact.tint-gold   { box-shadow: 0 0 14px rgba(212,175,55,0.18); }
  .form-card .glow-frame.compact.tint-purple { box-shadow: 0 0 14px rgba(181,131,240,0.20); }
  .form-card .glow-frame.compact.tint-mono   { box-shadow: 0 0 14px rgba(255,255,255,0.10); }
  .form-card input:focus, .form-card textarea:focus, .form-card select:focus {
    outline: none;
    background: #0e0e12;
  }

  /* Kind card outline + pulsing glow (Pricing-button style border, kind-colored) */
  .kind-card.kind-blue {
    border: 1px solid var(--blue) !important;
    animation: kindGlowBlue 3.4s ease-in-out infinite;
  }
  .kind-card.kind-gold {
    border: 1px solid var(--gold) !important;
    animation: kindGlowGold 3.4s ease-in-out infinite;
  }
  .kind-card.kind-purple {
    border: 1px solid var(--purple) !important;
    animation: kindGlowPurple 3.4s ease-in-out infinite;
  }
  .kind-card.kind-mono {
    border: 1px solid rgba(255,255,255,0.55) !important;
    animation: kindGlowMono 3.4s ease-in-out infinite;
  }
  @keyframes kindGlowBlue {
    0%, 100% { box-shadow: 0 1px 0 rgba(255,255,255,0.06) inset, 0 24px 50px rgba(0,0,0,0.45), 0 0 18px rgba(122,184,255,0.25); }
    50%      { box-shadow: 0 1px 0 rgba(255,255,255,0.06) inset, 0 24px 50px rgba(0,0,0,0.45), 0 0 36px rgba(122,184,255,0.6); }
  }
  @keyframes kindGlowGold {
    0%, 100% { box-shadow: 0 1px 0 rgba(255,255,255,0.06) inset, 0 24px 50px rgba(0,0,0,0.45), 0 0 18px rgba(212,175,55,0.25); }
    50%      { box-shadow: 0 1px 0 rgba(255,255,255,0.06) inset, 0 24px 50px rgba(0,0,0,0.45), 0 0 36px rgba(244,208,63,0.65); }
  }
  @keyframes kindGlowPurple {
    0%, 100% { box-shadow: 0 1px 0 rgba(255,255,255,0.06) inset, 0 24px 50px rgba(0,0,0,0.45), 0 0 18px rgba(181,131,240,0.25); }
    50%      { box-shadow: 0 1px 0 rgba(255,255,255,0.06) inset, 0 24px 50px rgba(0,0,0,0.45), 0 0 36px rgba(220,184,255,0.65); }
  }
  @keyframes kindGlowMono {
    0%, 100% { box-shadow: 0 1px 0 rgba(255,255,255,0.06) inset, 0 24px 50px rgba(0,0,0,0.45), 0 0 18px rgba(255,255,255,0.2); }
    50%      { box-shadow: 0 1px 0 rgba(255,255,255,0.06) inset, 0 24px 50px rgba(0,0,0,0.45), 0 0 36px rgba(255,255,255,0.5); }
  }
  .form-card .pill-eyebrow { align-self: flex-start; }
  .field-label {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--text-faint);
    text-transform: uppercase;
    letter-spacing: 1.4px;
  }

  .composer-foot {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
  }
  .word-count {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-faint);
    letter-spacing: 0.4px;
  }

  .voice-hint-btn {
    position: absolute;
    bottom: 8px;
    right: 0;
    background: rgba(255,255,255,0.05);
    border: 1px solid var(--border);
    color: var(--text-muted);
    padding: 6px 12px;
    border-radius: 999px;
    cursor: pointer;
    font-family: var(--font-sans);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.3px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    transition: all 0.15s ease;
  }
  .voice-hint-btn:hover {
    background: rgba(255,255,255,0.08);
    color: var(--text);
    border-color: var(--gold-line);
  }

  .submit-row {
    display: flex;
    justify-content: center;
    margin-top: 22px;
  }
  .parse-bolt {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 12px 22px;
    border-radius: 999px;
    background: linear-gradient(180deg, #F4D03F 0%, #D4AF37 55%, #A8861A 100%);
    border: 1px solid rgba(212,175,55,0.6);
    color: #0a0a0c;
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.2px;
    cursor: pointer;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.5), 0 8px 22px rgba(212,175,55,0.32);
    transition: transform 0.15s ease, box-shadow 0.2s ease;
  }
  .parse-bolt:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.6), 0 12px 28px rgba(244,208,63,0.5);
  }
  .parse-bolt:disabled { opacity: 0.4; cursor: not-allowed; }
  .parse-bolt svg { display: block; }

  .primary-btn {
    background: linear-gradient(180deg, #F4D03F 0%, #D4AF37 55%, #A8861A 100%);
    border: 1px solid rgba(212,175,55,0.6);
    color: #0a0a0c;
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.2px;
    padding: 10px 20px;
    border-radius: 10px;
    cursor: pointer;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.5), 0 6px 18px rgba(212,175,55,0.25);
    transition: transform 0.1s ease, box-shadow 0.2s ease;
  }
  .primary-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.6), 0 10px 28px rgba(244,208,63,0.45);
  }
  .primary-btn:disabled { opacity: 0.45; cursor: not-allowed; }

  .primary-btn.gold-btn {
    background: linear-gradient(180deg, #F4D03F 0%, #D4AF37 55%, #A8861A 100%);
    border-color: rgba(212,175,55,0.6);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.5), 0 6px 18px rgba(212,175,55,0.3);
  }
  .primary-btn.gold-btn:hover:not(:disabled) {
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.6), 0 10px 28px rgba(244,208,63,0.5);
  }
  .primary-btn.blue-btn {
    background: linear-gradient(180deg, #B6D7FF 0%, #7AB8FF 55%, #4A90E2 100%);
    border: 1px solid rgba(122,184,255,0.6);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.5), 0 6px 18px rgba(122,184,255,0.32);
  }
  .primary-btn.blue-btn:hover:not(:disabled) {
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.6), 0 10px 28px rgba(122,184,255,0.55);
  }
  .primary-btn.purple-btn {
    background: linear-gradient(180deg, #DCB8FF 0%, #B583F0 55%, #7C4DC9 100%);
    border: 1px solid rgba(181,131,240,0.6);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.5), 0 6px 18px rgba(181,131,240,0.32);
  }
  .primary-btn.purple-btn:hover:not(:disabled) {
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.6), 0 10px 28px rgba(181,131,240,0.55);
  }

  .ghost-btn {
    background: transparent;
    border: 1px solid var(--border-strong);
    color: var(--text-muted);
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 600;
    padding: 10px 18px;
    border-radius: 10px;
    cursor: pointer;
    transition: color 0.15s ease, border-color 0.15s ease;
  }
  .ghost-btn:hover { color: var(--text); border-color: var(--text-muted); }

  /* ─── Proposal page ─── */
  .proposal-wrap { display: flex; flex-direction: column; gap: 22px; }
  .collapsed-spark {
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .spark-text {
    margin: 0;
    color: var(--text-muted);
    font-family: var(--font-sans);
    font-size: 14px;
    line-height: 1.5;
  }
  .proposal-card {
    padding: 28px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .proposal-title {
    font-family: var(--font-serif);
    font-size: 28px;
    font-weight: 400;
    margin: 4px 0 8px;
    letter-spacing: -0.4px;
    color: var(--text);
  }

  .entity-card {
    padding: 14px 16px;
    border-radius: 10px;
    box-shadow: inset 0 0 0 1px rgba(255,255,255,0.04);
  }
  .entity-card.audience {
    background: rgba(122,184,255,0.05);
    border: 2px solid var(--blue);
    box-shadow: 0 0 14px rgba(122,184,255,0.12), inset 0 0 0 1px rgba(122,184,255,0.08);
  }
  .entity-card.offer {
    background: rgba(212,175,55,0.05);
    border: 2px solid var(--gold);
    box-shadow: 0 0 14px rgba(212,175,55,0.12), inset 0 0 0 1px rgba(212,175,55,0.08);
  }
  .entity-eyebrow {
    font-family: var(--font-mono);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.8px;
  }
  .audience-eyebrow { color: var(--blue); }
  .offer-eyebrow    { color: var(--gold); }
  .entity-title {
    font-family: var(--font-serif);
    font-size: 18px;
    font-weight: 400;
    color: var(--text);
    margin-top: 4px;
    letter-spacing: -0.2px;
  }
  .entity-line {
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--text-muted);
    margin-top: 6px;
    line-height: 1.45;
  }

  .focus-block { margin-top: 4px; }
  .block-eyebrow {
    display: block;
    font-family: var(--font-mono);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1.8px;
    color: var(--text-faint);
    margin-bottom: 10px;
  }
  .focus-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .focus-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: 10px;
    border: 1px solid var(--border);
  }
  .focus-row.is-audience {
    background: var(--blue-soft);
    border-color: var(--blue-line);
  }
  .focus-row.is-offer {
    background: var(--gold-soft);
    border-color: var(--gold-line);
  }
  .focus-row.is-unsorted {
    background: rgba(255,255,255,0.02);
  }
  .focus-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .focus-row.is-audience .focus-dot { background: var(--blue); box-shadow: 0 0 6px rgba(122,184,255,0.6); }
  .focus-row.is-offer    .focus-dot { background: var(--gold); box-shadow: 0 0 6px rgba(212,175,55,0.6); }
  .focus-row.is-unsorted .focus-dot { background: var(--text-faint); }
  .focus-meat {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .focus-kind {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--text-faint);
    text-transform: uppercase;
    letter-spacing: 1.2px;
  }
  .focus-content {
    font-family: var(--font-sans);
    font-size: 13px;
    color: var(--text);
    line-height: 1.4;
  }
  .conf-chip {
    font-family: var(--font-mono);
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 3px 8px;
    border-radius: 999px;
  }
  .conf-chip.high   { background: rgba(124,217,146,0.15); color: var(--green); }
  .conf-chip.medium { background: rgba(212,175,55,0.15);  color: var(--gold); }
  .conf-chip.low    { background: rgba(227,100,100,0.15); color: var(--red); }

  .proposal-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }

  .error {
    color: var(--red);
    font-family: var(--font-sans);
    font-size: 12px;
    margin-top: 12px;
    padding: 10px 12px;
    background: rgba(227,100,100,0.08);
    border: 1px solid rgba(227,100,100,0.25);
    border-radius: 8px;
  }

  /* ─── Voice modal ─── */
  .voice-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.6);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: fadeIn 0.2s ease;
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  .voice-modal {
    padding: 28px;
    max-width: 480px;
    width: 100%;
    max-height: 85vh;
    overflow-y: auto;
    animation: modalIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  }
  @keyframes modalIn {
    from { opacity: 0; transform: translateY(8px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0)  scale(1); }
  }
  .voice-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }
  .voice-header h3 {
    font-family: var(--font-serif);
    color: var(--text);
    margin: 0;
    font-size: 22px;
    font-weight: 400;
    letter-spacing: -0.3px;
  }
  .voice-close {
    background: transparent;
    border: none;
    color: var(--text-faint);
    font-size: 24px;
    cursor: pointer;
    padding: 0 6px;
    line-height: 1;
  }
  .voice-close:hover { color: var(--text); }
  .voice-intro {
    color: var(--text-muted);
    font-size: 13px;
    line-height: 1.5;
    margin: 0 0 18px;
  }
  .voice-os-tabs {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
    margin-bottom: 18px;
  }
  .voice-os-tabs button {
    background: rgba(255,255,255,0.03);
    border: 1px solid var(--border);
    color: var(--text-muted);
    padding: 8px 4px;
    border-radius: 8px;
    cursor: pointer;
    font-family: var(--font-sans);
    font-size: 11px;
    font-weight: 500;
  }
  .voice-os-tabs button.active {
    background: rgba(212,175,55,0.15);
    border-color: var(--gold-line);
    color: var(--gold);
    font-weight: 700;
  }
  .voice-steps {
    background: rgba(0,0,0,0.3);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 16px 18px 16px 36px;
    margin-bottom: 16px;
  }
  .voice-steps ol {
    margin: 0;
    padding-left: 4px;
    color: var(--text-muted);
    font-size: 13px;
    line-height: 1.7;
  }
  .voice-steps li { margin-bottom: 6px; }
  .voice-steps li:last-child { margin-bottom: 0; }
  .voice-steps strong { color: var(--gold); font-weight: 600; }
  .voice-tip {
    color: var(--text-faint);
    font-size: 12px;
    line-height: 1.5;
    margin: 12px 0 0;
    font-style: italic;
  }
  .voice-tip strong { color: var(--text-muted); font-style: normal; font-weight: 600; }
  .voice-footer { display: flex; justify-content: flex-end; }
  .voice-got-it {
    background: linear-gradient(180deg, #F4D03F 0%, #D4AF37 55%, #A8861A 100%);
    color: #0a0a0c;
    border: none;
    padding: 10px 22px;
    border-radius: 10px;
    cursor: pointer;
    font-family: var(--font-sans);
    font-weight: 700;
    font-size: 13px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.5);
  }

  .brainstorm-launch {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    background: linear-gradient(180deg, rgba(212,175,55,0.08), rgba(212,175,55,0.04));
    border: 1px solid rgba(212,175,55,0.25);
    color: #f0efe9;
    padding: 10px 16px;
    border-radius: 999px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.4px;
    margin: 0 0 14px;
    transition: all 0.15s ease;
    box-shadow: none;
  }
  .brainstorm-launch:hover {
    background: linear-gradient(180deg, rgba(212,175,55,0.14), rgba(212,175,55,0.08));
    border-color: rgba(212,175,55,0.45);
    transform: none;
  }
  .brainstorm-launch .bs-icon { color: #d4af37; font-size: 14px; }
  .brainstorm-launch .bs-text { flex: 1; text-align: left; }
  .brainstorm-launch .bs-arrow { color: #d4af37; font-size: 14px; }

  .brainstorm-elvie-header {
    display: flex; gap: 12px; align-items: center;
    padding: 14px 18px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    margin-bottom: 0;
  }
  .brainstorm-elvie-avatar {
    width: 36px; height: 36px;
    border-radius: 50%;
    background: linear-gradient(180deg, #d4af37, #b8941f);
    display: flex; align-items: center; justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
  }
  .brainstorm-elvie-header > div {
    flex: 1;
  }
  .brainstorm-elvie-header h3 {
    margin: 0;
    font-family: 'DM Serif Display', serif;
    font-size: 18px;
    color: #f0efe9;
  }
  .brainstorm-elvie-subtitle {
    margin: 0;
    font-size: 11px;
    color: #9aa0a6;
    font-family: 'JetBrains Mono', monospace;
  }
  .brainstorm-elvie-close {
    flex-shrink: 0;
  }
  .bs-header-sub {
    padding-top: 10px;
    border-bottom: none;
  }
  .bs-header-sub .bs-sub {
    margin: 0 0 8px;
  }

  .bs-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 1000;
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    animation: fadeIn 0.2s ease;
  }
  .bs-modal {
    background: linear-gradient(180deg, rgba(20,18,22,0.95), rgba(12,11,14,0.95));
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 18px;
    padding: 0;
    max-width: 640px;
    width: 100%;
    max-height: 86vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 24px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05);
    animation: modalIn 0.25s cubic-bezier(0.16, 1, 0.3, 1);
    overflow: hidden;
  }
  .bs-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 22px 24px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .bs-header h3 {
    margin: 0 0 4px;
    font-family: 'DM Serif Display', serif;
    font-size: 22px;
    font-weight: 400;
    color: #f0efe9;
    letter-spacing: -0.01em;
  }
  .bs-sub {
    color: #9aa0a6;
    font-size: 12px;
    margin: 0;
    line-height: 1.5;
  }
  .bs-close {
    background: transparent; border: none;
    color: #888; font-size: 22px; cursor: pointer;
    padding: 0 8px; line-height: 1;
    margin: 0; box-shadow: none;
  }
  .bs-close:hover { color: #fff; transform: none; background: transparent; }

  .bs-thread {
    flex: 1;
    overflow-y: auto;
    padding: 20px 24px;
    display: flex; flex-direction: column; gap: 14px;
    min-height: 240px;
  }
  .bs-msg { display: flex; gap: 10px; align-items: flex-start; }
  .bs-msg.bs-user { justify-content: flex-end; }
  .bs-msg.bs-user .bs-bubble {
    background: rgba(212,175,55,0.12);
    border: 1px solid rgba(212,175,55,0.2);
    color: #f0efe9;
    max-width: 78%;
  }
  .bs-avatar {
    width: 28px; height: 28px; flex-shrink: 0;
    border-radius: 50%;
    background: linear-gradient(135deg, #d4af37, #a8861a);
    color: #0a0a0c;
    font-size: 13px;
    display: flex; align-items: center; justify-content: center;
    font-weight: 600;
  }
  .bs-bubble {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.06);
    color: #d8d8dc;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 13.5px;
    line-height: 1.55;
    max-width: 78%;
    white-space: pre-wrap;
  }
  .bs-typing { display: inline-flex; gap: 4px; padding: 12px 16px; }
  .bs-typing span {
    width: 6px; height: 6px; border-radius: 50%;
    background: rgba(212,175,55,0.6);
    animation: bsdot 1.2s infinite ease-in-out both;
  }
  .bs-typing span:nth-child(2) { animation-delay: 0.18s; }
  .bs-typing span:nth-child(3) { animation-delay: 0.36s; }
  @keyframes bsdot {
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
    40% { transform: scale(1); opacity: 1; }
  }
  .bs-error {
    color: #d48484;
    font-size: 12px;
    background: rgba(200,100,100,0.08);
    border: 1px solid rgba(200,100,100,0.18);
    padding: 8px 12px;
    border-radius: 8px;
  }

  .bs-input-row {
    border-top: 1px solid rgba(255,255,255,0.06);
    padding: 14px 18px;
    display: flex; gap: 10px;
    align-items: flex-end;
  }
  .bs-input-row textarea {
    flex: 1;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.08);
    color: #f4f4f6;
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 13.5px;
    resize: none;
    margin: 0;
    font-family: inherit;
    line-height: 1.5;
  }
  .bs-input-row textarea:focus { outline: none; border-color: rgba(212,175,55,0.4); }
  .bs-send {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c;
    border: none;
    padding: 10px 20px;
    border-radius: 10px;
    cursor: pointer;
    font-weight: 600;
    font-size: 13px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.25);
    margin: 0;
  }
  .bs-send:disabled { opacity: 0.4; cursor: not-allowed; }

  .bs-ready-card {
    border-top: 1px solid rgba(212,175,55,0.3);
    padding: 18px 22px 22px;
    background: linear-gradient(180deg, rgba(212,175,55,0.06), transparent);
  }
  .bs-ready-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 1.6px;
    text-transform: uppercase;
    color: #d4af37;
    margin-bottom: 8px;
  }
  .bs-ready-title {
    font-family: 'DM Serif Display', serif;
    font-size: 18px;
    color: #f0efe9;
    margin-bottom: 8px;
    letter-spacing: -0.01em;
  }
  .bs-ready-text {
    color: #f0efe9;
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 14px;
    padding: 12px 14px;
    background: rgba(0,0,0,0.35);
    border: 1px solid rgba(212,175,55,0.18);
    border-radius: 10px;
    white-space: pre-wrap;
  }
  .bs-ready-actions { display: flex; gap: 10px; flex-wrap: wrap; }
  .bs-primary {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c; border: none;
    padding: 10px 20px; border-radius: 8px; cursor: pointer;
    font-weight: 600; font-size: 13px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.25);
  }
  .bs-secondary {
    background: rgba(255,255,255,0.04);
    color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.1);
    padding: 10px 20px; border-radius: 8px; cursor: pointer;
    font-size: 13px;
  }
  .spark-proposal-header {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 14px;
  }
  .spark-proposal-header h3 {
    margin: 0;
    font-family: 'DM Serif Display', serif;
    font-size: 18px;
    color: #f0efe9;
  }
  .spark-back-btn {
    background: rgba(255,255,255,0.05);
    color: #d8d8dc;
    border: 1px solid rgba(255,255,255,0.1);
    padding: 6px 12px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 11px;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 0.8px;
  }
  .spark-back-btn:hover { background: rgba(212,175,55,0.1); border-color: rgba(212,175,55,0.3); color: #d4af37; }
  .spark-original {
    margin: 0 0 16px;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 8px;
    padding: 8px 12px;
  }
  .spark-original summary {
    cursor: pointer;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #9aa0a6;
  }
  .spark-original summary:hover { color: #d4af37; }
  .spark-original-text {
    margin: 8px 0 0;
    padding: 8px;
    background: rgba(0,0,0,0.3);
    color: #d8d8dc;
    font-family: inherit;
    font-size: 12px;
    white-space: pre-wrap;
    word-wrap: break-word;
    max-height: 240px;
    overflow-y: auto;
    border-radius: 6px;
  }
  .spark-redirect {
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid rgba(212,175,55,0.2);
  }
  .spark-redirect-label {
    display: block;
    color: #d4af37;
    font-size: 12px;
    font-style: italic;
    margin-bottom: 6px;
  }
  .spark-redirect-row {
    display: flex; gap: 8px;
  }
  .spark-redirect-input {
    flex: 1;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(212,175,55,0.25);
    color: #f4f4f6;
    padding: 8px 10px;
    border-radius: 8px;
    font-size: 12px;
    font-family: inherit;
    resize: vertical;
  }
  .spark-redirect-input:focus { outline: none; border-color: rgba(212,175,55,0.5); }
  .spark-redirect-btn {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c;
    border: none;
    padding: 0 16px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    font-size: 12px;
    white-space: nowrap;
  }
  .spark-redirect-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .prop-card {
    display: flex; align-items: flex-start; gap: 12px;
    width: 100%;
    padding: 14px;
    background: rgba(255,255,255,0.02);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 10px;
    cursor: pointer;
    text-align: left;
    margin-bottom: 8px;
    transition: all 0.12s ease;
    color: inherit;
  }
  .prop-card:hover {
    background: rgba(212,175,55,0.06);
    border-color: rgba(212,175,55,0.3);
  }
  .prop-card.selected {
    background: rgba(212,175,55,0.12);
    border-color: rgba(212,175,55,0.5);
  }
  .prop-check {
    width: 22px; height: 22px;
    border-radius: 50%;
    border: 1.5px solid rgba(212,175,55,0.4);
    display: flex; align-items: center; justify-content: center;
    font-size: 12px;
    color: #d4af37;
    flex-shrink: 0;
  }
  .prop-card.selected .prop-check {
    background: #d4af37;
    color: #0a0a0c;
  }
  .prop-body-wrap { flex: 1; min-width: 0; }
  .prop-kind {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1.4px;
    color: #9aa0a6;
    margin-bottom: 4px;
  }
  .prop-card.selected .prop-kind { color: #d4af37; }
  .prop-title {
    font-family: 'DM Serif Display', serif;
    font-size: 16px;
    color: #f0efe9;
    margin: 0 0 4px;
  }
  .prop-line, .prop-body {
    margin: 0;
    color: #d8d8dc;
    font-size: 13px;
    line-height: 1.5;
  }
  .prop-section-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1.4px;
    color: #9aa0a6;
    margin: 14px 0 6px;
  }
  .spark-actions {
    display: flex; gap: 8px; align-items: center;
    margin: 16px 0 12px;
    padding-top: 14px;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
  .spark-actions-spacer { flex: 1; }
  .spark-action-primary {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c;
    border: none;
    padding: 8px 16px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    font-size: 12px;
  }
  .spark-action-primary:disabled { opacity: 0.4; cursor: not-allowed; }
  .spark-action-secondary {
    background: rgba(255,255,255,0.05);
    color: #d8d8dc;
    border: 1px solid rgba(255,255,255,0.1);
    padding: 8px 12px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 11px;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 0.6px;
  }
  .spark-action-secondary:hover { background: rgba(212,175,55,0.1); border-color: rgba(212,175,55,0.3); color: #d4af37; }
  .spark-action-tertiary {
    background: none; color: #9aa0a6;
    border: none;
    padding: 8px 4px;
    cursor: pointer;
    font-size: 11px;
    text-decoration: underline;
  }
  .spark-action-tertiary:hover { color: #d48484; }

  .spark-quick-redirects {
    display: flex; gap: 6px; align-items: center; flex-wrap: wrap;
    margin-bottom: 12px;
    padding: 10px 12px;
    background: rgba(212,175,55,0.04);
    border: 1px solid rgba(212,175,55,0.15);
    border-radius: 8px;
  }
  .spark-quick-label {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 1.2px;
    color: #d4af37;
    margin-right: 4px;
  }
  .spark-quick-btn {
    background: rgba(212,175,55,0.08);
    color: #d4af37;
    border: 1px solid rgba(212,175,55,0.3);
    padding: 4px 10px;
    border-radius: 999px;
    cursor: pointer;
    font-size: 11px;
    font-family: 'JetBrains Mono', monospace;
  }
  .spark-quick-btn:hover { background: rgba(212,175,55,0.18); border-color: rgba(212,175,55,0.5); }
</style>
